import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { MODEL_CONFIG, normalizeProvider } from './modelConfig.js'

export class AiRequestError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'AiRequestError'
    this.code = code
  }
}

const readErrorMessage = async (response, fallback) => {
  const raw = await response.text().catch(() => '')
  if (!raw) return fallback
  try {
    const data = JSON.parse(raw)
    return data.error?.message || data.message || fallback
  } catch {
    return raw.slice(0, 300) || fallback
  }
}

const fetchWithTimeout = async (url, options, { timeoutMs = 45000, retries = 1, signal } = {}) => {
  let lastError
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController()
    const abort = () => controller.abort(signal?.reason)
    signal?.addEventListener('abort', abort, { once: true })
    const timeout = setTimeout(() => controller.abort(new DOMException('Timeout', 'AbortError')), timeoutMs)
    try {
      return await fetch(url, { ...options, referrerPolicy: 'no-referrer', signal: controller.signal })
    } catch (error) {
      lastError = error
      if (signal?.aborted) throw new AiRequestError('cancelled', '请求已取消。')
      if (attempt >= retries) {
        if (error.name === 'AbortError') throw new AiRequestError('timeout', 'AI 响应超时，请稍后重试。')
        throw new AiRequestError('network', '网络请求失败，请检查网络或浏览器跨域限制。')
      }
    } finally {
      clearTimeout(timeout)
      signal?.removeEventListener('abort', abort)
    }
  }
  throw lastError
}

export const callModel = async ({ provider, model, apiKey, systemPrompt, prompt, temperature = 0.25, signal }) => {
  const normalized = normalizeProvider(provider)
  if (!apiKey?.trim()) throw new AiRequestError('auth', '请先在设置中填写 API Key。')
  if (!MODEL_CONFIG[normalized].models.includes(model)) throw new AiRequestError('config', '所选模型已失效，请在设置中重新选择。')

  let url
  let headers = { 'Content-Type': 'application/json' }
  let body
  if (normalized === 'deepseek') {
    url = 'https://api.deepseek.com/v1/chat/completions'
    headers = { ...headers, Authorization: `Bearer ${apiKey.trim()}` }
    body = { model, temperature, messages: [{ role: 'system', content: systemPrompt }, { role: 'user', content: prompt }] }
  } else {
    url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(apiKey.trim())}`
    body = {
      systemInstruction: { parts: [{ text: systemPrompt }] },
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: { temperature },
    }
  }
  const response = await fetchWithTimeout(url, { method: 'POST', headers, body: JSON.stringify(body) }, { signal })
  if (!response.ok) {
    if ([401, 403].includes(response.status)) throw new AiRequestError('auth', 'API Key 无效或无权使用该模型。')
    if ([429, 503].includes(response.status)) throw new AiRequestError('busy', `${MODEL_CONFIG[normalized].shortLabel} 当前繁忙，请稍后重试。`)
    throw new AiRequestError('api', await readErrorMessage(response, `AI 请求失败（${response.status}）。`))
  }
  const data = await response.json()
  const text = normalized === 'gemini'
    ? data.candidates?.[0]?.content?.parts?.map((part) => part.text || '').join('\n')
    : data.choices?.[0]?.message?.content
  if (!text?.trim()) throw new AiRequestError('empty', '模型没有返回有效内容。')
  return text
}

export const renderMarkdown = (markdown) => DOMPurify.sanitize(marked.parse(markdown || ''), {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'iframe', 'object', 'embed'],
})
